import React from 'react';
import { PureComponent } from '../../../../dispatcher/modules';
import {
  MultiSelectBlurEvent,
  MultiSelectFilterChangeEvent,
  MultiSelectPageChangeEvent
} from '@progress/kendo-react-dropdowns';
import _, { uniqueId } from 'lodash';
import {
  PUZZLE_REMOTE_MULTISELECT_ALL_DATA_PROPS, 
  PUZZLE_REMOTE_MULTISELECT_ALL_DATA_STATE
} from './Types';
import { Position } from '../../../../dispatcher/Types';
import { filterBy } from '@progress/kendo-data-query';
import PuzzleMultiselect from './PuzzleMultiselect';
import {
  ComboBoxFilterChangeEvent, 
  ComboBoxPageChangeEvent
} from '@progress/kendo-react-dropdowns/dist/npm/ComboBox/ComboBoxProps';
import { VirtualScrollStatic } from './VirtualScrollStatic';

/**
 * Remote all Data loaded after mounting and uses multiselect virtualization and filtering
 * @author Zarko Radojevic
 */
class PuzzleRemoteMultiselectAllData<P = {}> extends PureComponent<PUZZLE_REMOTE_MULTISELECT_ALL_DATA_PROPS & P, PUZZLE_REMOTE_MULTISELECT_ALL_DATA_STATE> {
  allData: any[];
  filteredData: any[];
  virtualizationMoreThen: number = 100;
  comboRef: any;

  constructor(props) {
    super(props);
    this.moduleId = uniqueId('puzzle-multiselect_');
    if (props.remoteVirtualScrolling) {
      this.state = {
        skip: 0,
        total: 0,
        subsetData: []
      };
    }
  }

  componentDidMount() {
    super.componentDidMount();
    const { remoteVirtualScrolling, data } = this.props;
    if (remoteVirtualScrolling) {
      const { requestConfig } = remoteVirtualScrolling;
      if (requestConfig) {
        this.loadInitialRemoteData();
      } else if (!_.isEmpty(data)) {
        this.loadData(this.parseData(data));
      }
    }
    let componentRef = this.comboRef.comboBoxRef;
    if (!componentRef) {
      componentRef = this.comboRef.multiselectRef;
    }
    const vs = componentRef.base.vs;
    const total = vs.total;
    const enabled = vs.enabled;
    const pageSize = vs.pageSize;
    const containerHeight = vs.containerHeight;
    componentRef.base.vs = new VirtualScrollStatic();
    componentRef.base.vs.enabled = enabled;
    componentRef.base.vs.total = total;
    componentRef.base.vs.pageSize = pageSize;
    componentRef.base.vs.containerHeight = containerHeight;
    componentRef.base.vs.PageChange = componentRef.base.pageChange;
  }

  /**
   * loadInitialRemoteData used for initial fetching data for multiselect component
   */
  loadInitialRemoteData = () => {
    const { remoteVirtualScrolling } = this.props;
    if (remoteVirtualScrolling && remoteVirtualScrolling.requestConfig) {
      const { requestConfig: { url, method, params } } = remoteVirtualScrolling;
      this.dispatch('initialMenuLoadData', {
        stack: true,
        pos: Position.end
      }, {
        menuModuleId: this.moduleId,
        menuModuleName: this.moduleName,
        url,
        method,
        params
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (!_.isEqual(this.props?.remoteVirtualScrolling?.requestConfig, prevProps?.remoteVirtualScrolling?.requestConfig)) {
      this.loadInitialRemoteData();
    }
  }

  /**
   * Parses data
   * @param data
   */
  parseData = (data) => {
    let parsedData = data;
    const { remoteVirtualScrolling } = this.props;
    if (remoteVirtualScrolling) {
      const { parseData } = remoteVirtualScrolling;
      if (parseData) {
        parsedData = parseData(data);
      }
    }
    return parsedData;
  }

  /**
   * Loads data
   * @param dataCache
   * @param isNoFirstLoad
   */
  loadData = (dataCache, isNoFirstLoad?) => {
    const { remoteVirtualScrolling } = this.props;
    const { pageSize = 20 } = remoteVirtualScrolling;
    if (!isNoFirstLoad) {
      this.allData = [...dataCache];
      this.filteredData = [...this.allData];
    }
    const total = this.filteredData.length;
    this.setState({
      subsetData: dataCache.slice(0, total > this.virtualizationMoreThen ? pageSize : total),
      total: this.filteredData.length,
      skip: 0
    });
  }
  onRemoteVirtualScrollingPageChange = (event: MultiSelectPageChangeEvent | ComboBoxPageChangeEvent):void => {
    let skip = event.page.skip;
    let take = event.page.take;
    const total = this.filteredData.length;
    const newSubsetData = this.filteredData.slice(skip, total > this.virtualizationMoreThen ? skip + take : total);
    this.setState({
      skip,
      subsetData: newSubsetData
    });
  }

  onRemoteVirtualScrollingFilterChange = (event: MultiSelectFilterChangeEvent | ComboBoxFilterChangeEvent):void => {
    const filter = event.filter;

    if (this.props.isFilterByStart) {
      filter['operator'] = 'startswith';
    }
    const { remoteVirtualScrolling } = this.props;
    const { pageSize = 20 } = remoteVirtualScrolling;
    this.filteredData = filterBy(this.allData.slice(), filter);
    const total = this.filteredData.length;
    const newSubsetData = this.filteredData.slice(0, total > this.virtualizationMoreThen ? pageSize : total);
    this.setState({
      subsetData: newSubsetData,
      skip: 0,
      total: this.filteredData.length
    });
  };

  /**
   * Set new data when multiselect lose focus
   * @param {MultiSelectBlurEvent} e
   */
  private handleBlur = (e:MultiSelectBlurEvent):void => {
    const { onBlur, remoteVirtualScrolling } = this.props;

    if (remoteVirtualScrolling?.filterable) {
      const { pageSize = 20 } = remoteVirtualScrolling;
      this.filteredData = this.allData;
      const total = this.filteredData.length;

      const currentSkip = this.state.skip;
      const newSubsetData = this.filteredData.slice(currentSkip, total > this.virtualizationMoreThen ? currentSkip + pageSize : total);

      this.setState({
        subsetData: newSubsetData,
        skip: currentSkip,
        total: this.filteredData.length
      });
    }

    if (onBlur) {
      onBlur(e);
    }
  }

  render() {
    const { total, skip } = this.state;
    let {
      remoteVirtualScrolling
    } = this.props;
    const { pageSize = 20 } = remoteVirtualScrolling;
    let props = { ...this.props };
    props = {
      ...props,
      data: this.state.subsetData,
      onFilterChange: this.onRemoteVirtualScrollingFilterChange
    };
    if (total > this.virtualizationMoreThen) {
      props = {
        ...props,
        virtual: {
          pageSize,
          total,
          skip
        },
        onPageChange: this.onRemoteVirtualScrollingPageChange
      };
    }

    let popupClass = 'data-preventrecorddeselect';

    if (total < 6) {
      popupClass += ' tp-remote-combo-box-popup';
    }

    return (
      <PuzzleMultiselect
        {...props}
        filterable={remoteVirtualScrolling.filterable}
        popupSettings={{
          popupClass
        }}
        onBlur={this.handleBlur}
        ref={(el: any) => {
          this.comboRef = el;
        }}
      />
    );
  }
}

export default PuzzleRemoteMultiselectAllData;
